import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, Select, Stack,
  Typography,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import { listCapDevices } from '../../utils/optimizeScopeUtils';
import { capsOfSerials, iperfMapInitialSerials, iperfTargetFromMapSelection } from '../../utils/mapSelectUtils';
import IperfTestSettingsFields from './IperfTestSettingsFields';
import MapSelectDialog from './MapSelectDialog';

const ALL = '__all__';

/**
 * CAPs and settings of a scheduled iperf test: used by iperf schedules and
 * by optimize schedules that run an iperf test first. "All CAPs" is kept
 * as "all" and resolved when the test starts.
 *
 * @param {object} props
 * @param {object} props.value { allCaps, capSerials, focusSerials, settings } (see iperfTestFormFromRequest)
 * @param {(patch:object) => void} props.onChange
 * @param {Array} [props.devices] site devices, for the CAP picker
 * @param {string} [props.capsLabel] heading of the CAP picker
 */
export default function IperfTestTargetFields({ value, onChange, devices, capsLabel = 'CAPs' }) {
  const caps = useMemo(() => listCapDevices(devices || []), [devices]);
  const capBySerial = useMemo(() => new Map(caps.map((c) => [c.serial_number, c])), [caps]);
  const { allCaps, capSerials, focusSerials, settings } = value;
  const [mapOpen, setMapOpen] = useState(false);
  // Limit the test to the devices picked on the map (default), or measure
  // the whole tree of every CAP they belong to.
  const [limitToMap, setLimitToMap] = useState(true);
  const openMap = () => {
    setLimitToMap(focusSerials.length > 0 || allCaps || capSerials.length === 0);
    setMapOpen(true);
  };

  const onCapsChange = (e) => {
    const v = e.target.value;
    if (v.includes(ALL)) {
      onChange({ allCaps: !allCaps, capSerials: [] });
      return;
    }
    onChange({ allCaps: false, capSerials: v });
  };
  const missingCaps = !allCaps && caps.length > 0 ? capSerials.filter((sn) => !capBySerial.has(sn)) : [];
  const capLabel = (sn) => {
    const d = capBySerial.get(sn);
    return d?.name && d.name !== sn ? `${d.name} (${sn})` : sn;
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>{capsLabel}</Typography>
          <Button size="small" startIcon={<MapIcon />} onClick={openMap} disabled={(devices || []).length === 0}>
            Select on map
          </Button>
        </Stack>
        <FormControl size="small" fullWidth>
          {/* Always shrunk: the value text ("All CAPs") shows even when
              nothing is in the selection array, and an unshrunk label
              would sit on top of it. */}
          <InputLabel shrink>CAP Devices</InputLabel>
          <Select
            multiple
            notched
            label="CAP Devices"
            value={allCaps ? [] : capSerials}
            onChange={onCapsChange}
            renderValue={() => (allCaps
              ? `All CAPs${caps.length ? ` (${caps.length} now)` : ''}`
              : `${capSerials.length} CAP${capSerials.length === 1 ? '' : 's'}`)}
            displayEmpty
          >
            <MenuItem value={ALL}>
              <Checkbox checked={allCaps} />
              <ListItemText primary="All CAPs" secondary="Resolved when the test starts, so new CAPs are included" />
            </MenuItem>
            {caps.map((d) => (
              <MenuItem key={d.serial_number} value={d.serial_number} disabled={allCaps}>
                <Checkbox checked={allCaps || capSerials.includes(d.serial_number)} />
                <ListItemText primary={d.serial_number} secondary={d.name && d.name !== d.serial_number ? d.name : undefined} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Each CAP gets its own test run (one after another, or several at a time with Trees in parallel). A CAP
          that is down when the test starts is skipped.
        </Typography>
        {!allCaps && capSerials.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {capSerials.map((sn) => (
              <Chip
                key={sn}
                size="small"
                label={capLabel(sn)}
                color={missingCaps.includes(sn) ? 'error' : 'default'}
                variant={missingCaps.includes(sn) ? 'outlined' : 'filled'}
              />
            ))}
          </Stack>
        )}
        {missingCaps.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            CAP{missingCaps.length > 1 ? 's' : ''} {missingCaps.join(', ')} not found on this site; the run covers only
            the CAPs that exist.
          </Alert>
        )}
        {focusSerials.length > 0 && (
          <Alert
            severity="info"
            sx={{ mt: 1 }}
            action={<Button size="small" color="inherit" onClick={() => onChange({ focusSerials: [] })}>Remove limit</Button>}
          >
            Limited to {focusSerials.length} selected device(s) and their uplink paths.
          </Alert>
        )}
        <MapSelectDialog
          open={mapOpen}
          title="Select devices to measure"
          devices={devices}
          initialSerials={iperfMapInitialSerials(value, devices)}
          hint="The test runs on the CAP tree of every selected device."
          onClose={() => setMapOpen(false)}
          onApply={(serials) => {
            onChange(iperfTargetFromMapSelection(devices, serials, limitToMap));
            setMapOpen(false);
          }}
          renderOptions={(serials) => {
            const target = capsOfSerials(devices, serials);
            const limited = limitToMap && iperfTargetFromMapSelection(devices, serials, true).focusSerials.length > 0;
            return (
              <Box>
                <FormControlLabel
                  control={<Checkbox size="small" checked={limitToMap} onChange={(e) => setLimitToMap(e.target.checked)} />}
                  label={<Typography variant="body2">Test only the selected devices (and their uplink paths)</Typography>}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {target.length
                    ? `Measures ${target.length} CAP tree${target.length === 1 ? '' : 's'}: ${target.map(capLabel).join(', ')}${limited ? ', limited to the selected devices' : ', whole trees'}.`
                    : 'No CAP found for the selection yet.'}
                </Typography>
              </Box>
            );
          }}
        />
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Iperf test settings</Typography>
        <IperfTestSettingsFields
          value={settings}
          onChange={(patch) => onChange({ settings: { ...settings, ...patch } })}
          timingOpen
          treeCount={allCaps ? null : capSerials.length}
        />
      </Box>
    </Stack>
  );
}
